import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ─── Unsplash CDN image helper ─── */
const uu = (id, w = 600, h = 400) =>
  `https://images.unsplash.com/${id}?w=${w}&h=${h}&fit=crop&q=80&auto=format`;

/* ─── Image map: each item gets a unique, correct food image ─── */
const IMG = {
  // Classic - Beverages
  espresso:       uu('photo-1510707577719-ae7c14805e3a'),
  cappuccino:     uu('photo-1572442388796-11668a67e53d'),
  latte:          uu('photo-1461023058943-07fcbe16d735'),
  coldCoffee:     uu('photo-1517701550927-30cf4ba1dba5'),
  icedTeaLemon:   uu('photo-1556679343-c7306c1976bc'),
  icedTeaPeach:   uu('photo-1544145945-f90425340c7e'),
  limeSoda:       uu('photo-1513558161293-cdaf765ed2fd'),
  // Classic - Snacks
  vegSandwich:    uu('photo-1528735602780-2552fd46c7af'),
  cheeseBurger:   uu('photo-1568901346375-23c9450c58cd'),
  frenchFries:    uu('photo-1573080496219-bb080dd4f877'),
  garlicBread:    uu('photo-1619535860434-ba1d8fa12536'),
  // Classic - Desserts
  chocolateCake:  uu('photo-1578985545062-69928b1d9587'),
  brownie:        uu('photo-1606313564200-e75d5e30476c'),
  cheesecake:     uu('photo-1533134242443-d4fd215305ad'),
  // Trendy - Special Drinks
  oreoShake:      uu('photo-1572490122747-3968b75cc699'),
  kitkatShake:    uu('photo-1541658016709-82535e94bc69'),
  coldBrew:       uu('photo-1504630083234-14187a9df0f5'),
  mojitoMint:     uu('photo-1551538827-9c037cb4f32a'),
  mojitoBlue:     uu('photo-1560508179-b2c9a3f8e92b'),
  // Trendy - Quick Bites
  pizza:          uu('photo-1574071318508-1cdbab80d002'),
  pastaWhite:     uu('photo-1621996346565-e3dbc646d9a9'),
  pastaRed:       uu('photo-1563379926898-05f4575a45d8'),
  nachos:         uu('photo-1513456852971-30c0b8199d4d'),
  // Trendy - Sweet Treats
  donuts:         uu('photo-1527904324834-3bda86da6771'),
  waffles:        uu('photo-1562376552-0d160a2f238d'),
  pancakes:       uu('photo-1567620905732-2d1ec7ab7445'),
  // Healthy - Drinks
  greenTea:       uu('photo-1564890369478-c89ca6d9cde9'),
  detoxWater:     uu('photo-1536935338788-846bb9981813'),
  fruitJuice:     uu('photo-1600271886742-f049cd451bba'),
  // Healthy - Light Food
  vegSalad:       uu('photo-1512621776951-a57141f2eefd'),
  grilledSandwich:uu('photo-1528735602780-2552fd46c7af'),
  smoothieBowl:   uu('photo-1590301157890-4810ed352733'),
  // Category banners (wider)
  bannerBeverages: uu('photo-1495474472287-4d71bcdd2085', 1200, 500),
  bannerSnacks:    uu('photo-1565299624946-b28f40a0ae38', 1200, 500),
  bannerDesserts:  uu('photo-1488477181946-6428a0291777', 1200, 500),
  bannerSpecial:   uu('photo-1577805947697-89e18249d767', 1200, 500),
  bannerQuick:     uu('photo-1565299507177-b0ac66763828', 1200, 500),
  bannerSweet:     uu('photo-1495147466023-ac5c588e2e94', 1200, 500),
  bannerHealthy:   uu('photo-1490645935967-10de6ba17061', 1200, 500),
  bannerLight:     uu('photo-1498837167922-ddd27525d352', 1200, 500),
};

const menuData = {
  classic: {
    name: 'Classic Café',
    emoji: '☕',
    description: 'Timeless favorites crafted with care and tradition',
    heroImage: IMG.espresso,
    categories: [
      {
        name: 'Beverages',
        icon: '🥤',
        image: IMG.bannerBeverages,
        items: [
          { name: 'Espresso', desc: 'Rich, bold, and intense', image: IMG.espresso },
          { name: 'Cappuccino', desc: 'Creamy foam meets espresso', image: IMG.cappuccino },
          { name: 'Latte', desc: 'Smooth, velvety coffee with steamed milk', image: IMG.latte },
          { name: 'Cold Coffee', desc: 'Chilled and refreshing coffee blend', image: IMG.coldCoffee },
          { name: 'Iced Tea (Lemon)', desc: 'Cool citrus refreshment', image: IMG.icedTeaLemon },
          { name: 'Iced Tea (Peach)', desc: 'Sweet peach iced delight', image: IMG.icedTeaPeach },
          { name: 'Fresh Lime Soda', desc: 'Fizzy, tangy, and refreshing', image: IMG.limeSoda },
        ],
      },
      {
        name: 'Snacks',
        icon: '🍔',
        image: IMG.bannerSnacks,
        items: [
          { name: 'Veg Sandwich', desc: 'Fresh veggies in toasted bread', image: IMG.vegSandwich },
          { name: 'Cheese Burger', desc: 'Juicy patty with melted cheese', image: IMG.cheeseBurger },
          { name: 'French Fries', desc: 'Crispy golden potato fries', image: IMG.frenchFries },
          { name: 'Garlic Bread', desc: 'Warm, buttery, and garlicky', image: IMG.garlicBread },
        ],
      },
      {
        name: 'Desserts',
        icon: '🍰',
        image: IMG.bannerDesserts,
        items: [
          { name: 'Chocolate Cake', desc: 'Rich and decadent chocolate layers', image: IMG.chocolateCake },
          { name: 'Brownie with Ice Cream', desc: 'Warm brownie meets cold ice cream', image: IMG.brownie },
          { name: 'Cheesecake', desc: 'Creamy, smooth, and divine', image: IMG.cheesecake },
        ],
      },
    ],
  },
  trendy: {
    name: 'Trendy Café',
    emoji: '🧋',
    description: 'Modern flavors for the adventurous and bold',
    heroImage: IMG.oreoShake,
    categories: [
      {
        name: 'Special Drinks',
        icon: '🧋',
        image: IMG.bannerSpecial,
        items: [
          { name: 'Oreo Shake', desc: 'Cookies and cream blended bliss', image: IMG.oreoShake },
          { name: 'KitKat Shake', desc: 'Crunchy wafer chocolate shake', image: IMG.kitkatShake },
          { name: 'Cold Brew Coffee', desc: 'Slow-steeped for smooth intensity', image: IMG.coldBrew },
          { name: 'Mojito (Mint)', desc: 'Fresh minty citrus cooler', image: IMG.mojitoMint },
          { name: 'Mojito (Blueberry)', desc: 'Sweet blueberry twist on the classic', image: IMG.mojitoBlue },
        ],
      },
      {
        name: 'Quick Bites',
        icon: '🍕',
        image: IMG.bannerQuick,
        items: [
          { name: 'Margherita Pizza', desc: 'Classic tomato, mozzarella, and basil', image: IMG.pizza },
          { name: 'Pasta (White Sauce)', desc: 'Creamy alfredo perfection', image: IMG.pastaWhite },
          { name: 'Pasta (Red Sauce)', desc: 'Bold tomato-based Italian classic', image: IMG.pastaRed },
          { name: 'Loaded Nachos', desc: 'Crunchy chips with cheesy toppings', image: IMG.nachos },
        ],
      },
      {
        name: 'Sweet Treats',
        icon: '🍩',
        image: IMG.bannerSweet,
        items: [
          { name: 'Donuts', desc: 'Glazed, sprinkled, and irresistible', image: IMG.donuts },
          { name: 'Waffles', desc: 'Crispy golden with toppings', image: IMG.waffles },
          { name: 'Pancakes', desc: 'Fluffy stacks of morning happiness', image: IMG.pancakes },
        ],
      },
    ],
  },
  healthy: {
    name: 'Healthy Café',
    emoji: '🌿',
    description: 'Fresh, wholesome, and nourishing choices',
    heroImage: IMG.smoothieBowl,
    categories: [
      {
        name: 'Healthy Drinks',
        icon: '🥗',
        image: IMG.bannerHealthy,
        items: [
          { name: 'Green Tea', desc: 'Light, refreshing, and antioxidant-rich', image: IMG.greenTea },
          { name: 'Detox Water', desc: 'Infused with cucumber, lemon & mint', image: IMG.detoxWater },
          { name: 'Fresh Fruit Juice', desc: 'Seasonal fruits, freshly squeezed', image: IMG.fruitJuice },
        ],
      },
      {
        name: 'Light Food',
        icon: '🥙',
        image: IMG.bannerLight,
        items: [
          { name: 'Veg Salad', desc: 'Crispy, fresh, and colorful', image: IMG.vegSalad },
          { name: 'Grilled Sandwich', desc: 'Toasted with healthy fillings', image: IMG.grilledSandwich },
          { name: 'Smoothie Bowl', desc: 'Thick, fruity, and topped with goodness', image: IMG.smoothieBowl },
        ],
      },
    ],
  },
};

const tabs = [
  { key: 'classic', label: 'Classic', emoji: '☕' },
  { key: 'trendy', label: 'Trendy', emoji: '🧋' },
  { key: 'healthy', label: 'Healthy', emoji: '🌿' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

export default function Services() {
  const [activeTab, setActiveTab] = useState('classic');
  const activeMenu = menuData[activeTab];

  return (
    <div className="bg-cream-50">
      {/* Hero Banner */}
      <section className="relative h-[55vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <AnimatePresence mode="sync">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
          >
            <img
              src={activeMenu.heroImage}
              alt={activeMenu.name}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-espresso-950/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso-950/95 via-espresso-950/40 to-espresso-950/60" />

        <div className="relative z-10 text-center px-4">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="category-badge mb-6 inline-flex bg-white/10 border-white/20 text-white/80"
          >
            <span>🍽️</span> Our Menu
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Our <span className="text-gradient-gold">Menu</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-white/70 text-lg max-w-lg mx-auto"
          >
            Three unique collections designed for every mood and moment
          </motion.p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="sticky top-[60px] z-30 bg-white/90 backdrop-blur-xl border-b border-espresso-100/50 shadow-sm">
        <div className="container-custom">
          <div className="flex justify-center gap-2 py-4">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                id={`menu-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeTab === tab.key
                    ? 'bg-espresso-900 text-white shadow-lg shadow-espresso-900/20'
                    : 'bg-cream-100 text-espresso-600 hover:bg-cream-200'
                }`}
              >
                <span className="text-lg">{tab.emoji}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Content */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Menu Header */}
              <div className="text-center mb-16">
                <div className="text-5xl mb-4">{activeMenu.emoji}</div>
                <h2
                  className="text-3xl md:text-4xl font-bold text-espresso-900 mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {activeMenu.name}
                </h2>
                <p className="text-espresso-400 max-w-md mx-auto">{activeMenu.description}</p>
                <div className="divider-gold mx-auto mt-6" />
              </div>

              {/* Categories */}
              {activeMenu.categories.map((cat) => (
                <motion.div
                  key={cat.name}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-50px' }}
                  variants={staggerContainer}
                  className="mb-20 last:mb-0"
                >
                  {/* Category Banner */}
                  <motion.div
                    variants={fadeUp}
                    className="relative rounded-2xl overflow-hidden mb-10 h-52 md:h-64 group"
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-espresso-950/90 via-espresso-950/50 to-transparent" />
                    <div className="absolute inset-0 flex items-center px-8 md:px-12">
                      <div>
                        <span className="text-4xl mb-2 block">{cat.icon}</span>
                        <h3
                          className="text-3xl md:text-4xl font-bold text-white mb-2"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {cat.name}
                        </h3>
                        <p className="text-white/60 text-sm">{cat.items.length} delicious items</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Items Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cat.items.map((item, itemIdx) => {
                      const whatsappMessage = encodeURIComponent(`Hi Squeeze N Sip, I would like to order: ${item.name}`);
                      const whatsappUrl = `https://wa.me/917993305226?text=${whatsappMessage}`;

                      return (
                        <motion.a
                          key={item.name}
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variants={fadeUp}
                          custom={itemIdx}
                          className="group bg-white rounded-2xl overflow-hidden border border-espresso-100/50 hover:border-amber-400/30 hover:shadow-xl hover:shadow-amber-400/5 transition-all duration-500 cursor-pointer block"
                        >
                          {/* Item Image */}
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={item.image}
                              alt={item.name}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.099.824zm-3.423-14.416c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm.082 21.378c-1.464 0-2.898-.375-4.159-1.085l-4.667 1.225 1.246-4.549c-.779-1.3-1.189-2.774-1.189-4.298 0-5.161 4.197-9.359 9.359-9.359 5.161 0 9.358 4.197 9.358 9.359 0 5.162-4.197 9.359-9.359 9.359z" />
                              </svg>
                            </div>
                            <div className="absolute top-3 right-3 w-3 h-3 rounded-full bg-amber-400 shadow-lg shadow-amber-400/40" />
                          </div>
                          {/* Item Info */}
                          <div className="p-5 flex flex-col items-start justify-between">
                            <div>
                              <h4
                                className="font-bold text-espresso-900 text-lg group-hover:text-amber-600 transition-colors duration-300 mb-1"
                                style={{ fontFamily: "'Playfair Display', serif" }}
                              >
                                {item.name}
                              </h4>
                              <p className="text-espresso-400 text-sm leading-relaxed mb-4">{item.desc}</p>
                            </div>
                            <span className="text-xs uppercase font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full border border-green-200 opacity-0 group-hover:opacity-100 transition-opacity">Order on WhatsApp</span>
                          </div>
                        </motion.a>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h3
              variants={fadeUp}
              className="text-2xl font-bold text-espresso-900 mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              ✨ Customize Your Order
            </motion.h3>
            <motion.div variants={fadeUp} custom={1} className="flex flex-wrap justify-center gap-3">
              {[
                '🍦 Ice Cream – Vanilla',
                '🍫 Cocoa Topping',
                '🫘 Extra Espresso Shot',
                '🥛 Oat Milk',
                '🍯 Honey Drizzle',
                '🫐 Fresh Berries',
              ].map((addon) => (
                <span
                  key={addon}
                  className="px-5 py-2.5 rounded-full bg-cream-100 text-espresso-700 text-sm font-medium hover:bg-amber-400/10 hover:border-amber-400/30 border border-transparent transition-all duration-300 cursor-default"
                >
                  {addon}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/hero-interior.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-espresso-950/85" />
        </div>
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeUp}
              className="text-3xl md:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Craving Something <span className="text-gradient-gold">Delicious?</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-white/60 mb-8 max-w-md mx-auto">
              Visit us near Kartigi Bus Stop or call us to place your order
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="flex flex-wrap justify-center gap-4">
              <a href="tel:7993305226" className="btn-primary">
                📞 Call to Order
              </a>
              <a
                href="https://maps.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary border-white/30 text-white hover:bg-white hover:text-espresso-900"
              >
                📍 Get Directions
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
